// "Teilen": who may open a board and with which role (read < write < admin). Board admins add,
// change and remove members; everyone else sees the list. A changed role applies the next time
// the person connects to the board.
import { api, ApiError, ROLE_LABEL, type Account, type Role } from "./account";
import { css, personColor } from "./palette";

interface Person { id: string; name: string; short: string }
interface Members { role: Role; members: { user: Person; role: Role }[] }

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};
const ROLES: Role[] = ["read", "write", "admin"];

function roleSelect(value: Role, label: string, remove: boolean) {
  const s = el("select", "filter-select");
  s.setAttribute("aria-label", label);
  for (const r of ROLES) {
    const o = el("option", undefined, ROLE_LABEL[r]);
    o.value = r;
    o.selected = r === value;
    s.appendChild(o);
  }
  if (remove) {
    const o = el("option", undefined, "Entfernen");
    o.value = "";
    s.appendChild(o);
  }
  return s;
}

export class SharePanel {
  private panel = document.getElementById("lw-share-panel") as HTMLElement;
  private data: Members | null = null;
  private people: Person[] = [];
  private message = "";

  constructor(private board: string, private me: Account, private onClose: () => void) {
    this.panel.addEventListener("keydown", (e) => { e.stopPropagation(); if (e.key === "Escape") this.close(); });
  }

  get isOpen() { return !this.panel.hidden; }

  async open() {
    this.panel.hidden = false;
    this.message = "";
    await this.load();
    this.panel.querySelector<HTMLElement>("select, button")?.focus();
  }

  close() {
    this.panel.hidden = true;
    this.onClose();
  }

  private async load() {
    try {
      [this.data, this.people] = await Promise.all([
        api<Members>(`./api/boards/${encodeURIComponent(this.board)}/members`),
        api<Person[]>("./api/people"),
      ]);
    } catch (e) {
      this.message = `Mitglieder konnten nicht geladen werden (${(e as Error).message}).`;
    }
    this.render();
  }

  private async setRole(user: string, role: Role | null) {
    try {
      await api(`./api/boards/${encodeURIComponent(this.board)}/members/${encodeURIComponent(user)}`, "PUT", { role });
      this.message = "";
      // my own role changed: reconnect with the new one
      if (user === this.me.id) return location.reload();
    } catch (e) {
      this.message = e instanceof ApiError && e.status === 409
        ? "Mindestens eine Person muss das Board verwalten."
        : `Nicht gespeichert (${(e as Error).message}).`;
    }
    await this.load();
  }

  private render() {
    const form = el("div", "fe-form");
    const head = el("div", "share-head");
    head.appendChild(el("h2", "fe-title", "Board teilen"));
    const x = el("button", "fe-icon share-close", "×");
    x.type = "button";
    x.setAttribute("aria-label", "Schliessen");
    x.addEventListener("click", () => this.close());
    head.appendChild(x);
    form.appendChild(head);
    const d = this.data;
    const admin = d?.role === "admin";
    if (d) {
      form.appendChild(el("p", "menu-hint", admin
        ? "Lesen: ansehen. Bearbeiten: Karten, Rahmen und Abhängigkeiten ändern. Verwalten: zusätzlich Mitglieder festlegen."
        : `Deine Rolle: ${ROLE_LABEL[d.role]}. Mitglieder ändern nur Personen, die das Board verwalten.`));
      const ul = el("ul", "share-list");
      for (const m of [...d.members].sort((a, b) => a.user.name.localeCompare(b.user.name, "de"))) {
        const li = el("li", "share-member");
        const av = el("span", "avatar", m.user.short);
        av.style.background = css(personColor(m.user.short));
        const name = el("span", "share-name", m.user.name);
        if (m.user.id === this.me.id) name.appendChild(el("span", "pp-you", "du"));
        li.append(av, name);
        if (admin) {
          const s = roleSelect(m.role, `Rolle von ${m.user.name}`, true);
          s.addEventListener("change", () => void this.setRole(m.user.id, (s.value || null) as Role | null));
          li.appendChild(s);
        } else {
          li.appendChild(el("span", "share-role", ROLE_LABEL[m.role]));
        }
        ul.appendChild(li);
      }
      if (!d.members.length) ul.appendChild(el("li", "menu-hint", "Noch keine Mitglieder – nur Personen mit Verwaltungsrecht für Lanewerk sehen dieses Board."));
      form.appendChild(ul);
      const others = this.people.filter((p) => !d.members.some((m) => m.user.id === p.id));
      if (admin && others.length) {
        const add = el("form", "fe-group share-add");
        add.appendChild(el("div", "menu-title", "Person hinzufügen"));
        const who = el("select", "filter-select");
        who.setAttribute("aria-label", "Person");
        for (const p of others.sort((a, b) => a.name.localeCompare(b.name, "de"))) {
          const o = el("option", undefined, `${p.name} (${p.short})`);
          o.value = p.id;
          who.appendChild(o);
        }
        const role = roleSelect("write", "Rolle", false);
        const btn = el("button", "primary", "Hinzufügen");
        btn.type = "submit";
        const row = el("div", "share-add-row");
        row.append(who, role, btn);
        add.appendChild(row);
        add.addEventListener("submit", (e) => { e.preventDefault(); void this.setRole(who.value, role.value as Role); });
        form.appendChild(add);
      }
      form.appendChild(el("p", "menu-hint", this.me.admin
        ? "Neue Personen lädst du unter „Personen“ ein. Personen mit Verwaltungsrecht für Lanewerk sehen alle Boards."
        : "Personen mit Verwaltungsrecht für Lanewerk sehen alle Boards."));
    }
    const msg = el("p", "fe-error", this.message);
    msg.setAttribute("role", "alert");
    form.appendChild(msg);
    this.panel.replaceChildren(form);
  }
}
